/**
 * Profile HTTP client for `apps/speak`.
 *
 * Wrapper sobre `/api/me/profile` (GET + PUT) armado con el cliente tipado
 * compartido. El PUT es un upsert de REEMPLAZO COMPLETO: los campos omitidos
 * se resetean. Por eso `updateProfile` recibe el perfil entero (el caller hace
 * get → merge → put).
 *
 * Los errores salen como `ProfileApiError` con `status` + `code`, no como un
 * `Error` opaco: el call site necesita distinguir "no llegó a la red"
 * (`network_error`, status 0 — lo que produce un fetch cortado, un backend
 * caído o el 302 cross-origin del gate del preview) de "la sesión venció"
 * (401) y de "el backend rechazó el body" (400). Sin esa distinción el
 * onboarding mostraba "No pudimos guardar" para las tres cosas y el fallo
 * quedaba indiagnosticable.
 */

import { createTypedApiClient } from '@orborix/auth-client';

export interface Profile {
  pronouns: string | null;
  birthdate: string | null;
  city: string | null;
  country: string | null;
  /** País de origen (ISO-3166 alpha-2). Deriva el registro regional del coach. */
  originCountry: string | null;
  /** Rango de edad declarado en onboarding (ej. '25-34'). NULL → saltado. */
  ageRange: string | null;
  /** Lengua nativa (ISO-639-1, ej. 'es'). Obligatoria desde 2026-10-09: nace
   *  con la cuenta y el onboarding la re-deriva del país. `null` sólo en
   *  cuentas sintéticas/de test sin perfil. */
  nativeLanguage: string | null;
  /** Motivaciones (slugs work|travel|studies|relocation|growth|culture), hasta 2. NULL → saltado. */
  learningGoals: string[] | null;
  aboutYou: string;
  assistantStyle: string;
  modulePrefs: Record<string, unknown>;
  updatedAt: string | null;
}

/**
 * Subset enviable al PUT (sin `updatedAt`, que lo maneja el server). La lengua
 * nativa es obligatoria y no se borra (2026-10-09): se manda para cambiarla y
 * se omite para conservarla — el backend rechaza `null`.
 */
export type ProfileInput = Omit<Profile, 'updatedAt' | 'nativeLanguage'> & {
  nativeLanguage?: string;
};

interface ProfileResponse {
  success: boolean;
  data: Profile;
}

// `abortError: 'wrap'`: ningún caller del perfil pasa `AbortSignal`, así que
// un abort acá sólo puede venir de la conexión cortándose — es red.
const client = createTypedApiClient({
  basePath: '/api/me/profile',
  errorName: 'ProfileApiError',
  abortError: 'wrap',
});

export const ProfileApiError = client.ApiError;

function isProfileResponse(value: unknown): value is ProfileResponse {
  if (typeof value !== 'object' || value === null) return false;
  const data = (value as { data?: unknown }).data;
  return typeof data === 'object' && data !== null;
}

/**
 * Desenvuelto propio (por eso compone `request` + `ensureOk` en vez de usar
 * `get`/`send`): un envelope sin `data` tiene que fallar RUIDOSO en vez de
 * devolver `undefined` disfrazado de perfil.
 */
async function profileRequest(init: RequestInit): Promise<Profile> {
  const res = await client.request('', init);
  await client.ensureOk(res);
  const body: unknown = await res.json().catch(() => null);
  if (!isProfileResponse(body)) {
    throw new ProfileApiError(
      'El servidor devolvió un perfil malformado.',
      res.status,
      'malformed_response',
    );
  }
  return body.data;
}

/** Carga el perfil del user (siempre devuelve uno; default vacío si no existe). */
export async function getProfile(): Promise<Profile> {
  return profileRequest({ method: 'GET' });
}

/** Reemplaza el perfil completo. Devuelve el perfil persistido. */
export async function updateProfile(input: ProfileInput): Promise<Profile> {
  return profileRequest({ method: 'PUT', body: JSON.stringify(input) });
}

/** Helper: setea solo `originCountry` preservando el resto del perfil. */
export async function setOriginCountry(code: string | null): Promise<Profile> {
  return patchProfile({ originCountry: code });
}

/**
 * Setea SÓLO los campos provistos, preservando el resto (get → merge → put).
 * El PUT es full-replace, así que omitir un campo del `partial` lo deja intacto
 * (lee el valor actual y lo reenvía). Usado por el onboarding para no pisar
 * datos que el user no tocó (p.ej. re-entrar y guardar sólo la motivación).
 */
export async function patchProfile(partial: Partial<ProfileInput>): Promise<Profile> {
  const current = await getProfile();
  const { updatedAt: _ignored, nativeLanguage, ...rest } = current;
  return updateProfile({ ...rest, ...(nativeLanguage ? { nativeLanguage } : {}), ...partial });
}
